import React from 'react';
import { Box, Tooltip, TooltipProps } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { hasDebugCookie } from '../../utilities/debugCookie';
import { isSuperadmin } from '../../utilities/superadmin';

const DEBUG_HEADER = '[Debug]';

export type SuperAdminTooltipProps = {
  /** Short label on the same row as [Debug]. */
  title?: string;
  /** Body shown under the [Debug] row. */
  content?: string;
  children: React.ReactElement;
  /** When true, render children without a tooltip even for admins. */
  isDisabled?: boolean;
  /**
   * Optional admin gate when AuthContext is unavailable (e.g. FullCalendar createRoot).
   * When omitted, uses `isSuperadmin(useAuth().user)`.
   */
  isEnabled?: boolean;
  placement?: TooltipProps['placement'];
};

type SuperAdminTooltipViewProps = Omit<SuperAdminTooltipProps, 'isEnabled'> & {
  isAdmin: boolean;
};

const SuperAdminTooltipView: React.FC<SuperAdminTooltipViewProps> = ({
  title,
  content,
  children,
  isDisabled = false,
  isAdmin,
  placement = 'top',
}) => {
  const headerLabel = title?.trim() ?? '';
  const body = content?.trim() ?? '';
  const shouldShowTooltip = isAdmin && hasDebugCookie();
  
  if (isDisabled || (!headerLabel && !body) || !shouldShowTooltip) {
    return children;
  }

  const header = headerLabel ? `${DEBUG_HEADER}  ${headerLabel}` : DEBUG_HEADER;
  const tooltipTitle = body ? `${header}\n${body}` : header;

  return (
    <Tooltip
      title={tooltipTitle}
      placement={placement}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'grey.900',
            whiteSpace: 'pre-wrap',
          },
        },
      }}
    >
      {/* Tooltip needs a child that can take a ref and listen for events. */}
      <Box
        component='div'
        data-testid='superadmin-debug-tooltip'
        sx={{
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
          maxWidth: '100%',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

const SuperAdminTooltipAuthed: React.FC<
  Omit<SuperAdminTooltipProps, 'isEnabled'>
> = props => {
  const { user } = useAuth();
  return (
    <SuperAdminTooltipView {...props} isAdmin={isSuperadmin(user)} />
  );
};

/**
 * Hover tooltip gated to MarketSmart admins (`isSuperadmin`) AND the DEBUG=True cookie.
 * Both conditions must be met to show the tooltip.
 * Non-admin users or users without the DEBUG cookie see children only - no tooltip chrome or title.
 */
const SuperAdminTooltip: React.FC<SuperAdminTooltipProps> = ({
  isEnabled,
  ...props
}) => {
  if (typeof isEnabled === 'boolean') {
    return <SuperAdminTooltipView {...props} isAdmin={isEnabled} />;
  }
  return <SuperAdminTooltipAuthed {...props} />;
};

export default SuperAdminTooltip;
